import {
	ApproveTemplateAsync,
	RejectTemplateAsync,
	SavePreSubmissionConfigurationDetail,
} from "../../../api/batchNotificationController";
import BatchMessageDetailModel, {
	PreSubmissionConfigurationModel,
} from "../../../models/BatchMessage/BatchMessageDetailModel";
import BatchMessageDraftModel, {
	Message,
} from "../../../models/BatchMessage/BatchMessageDraftModel";
import BatchMessageSchedulingDetailModel, {
	MessageDetail,
} from "../../../models/BatchMessage/BatchMessageSchedulingDetailModel";
import {
	BatchMessageTypeEnum,
	PreSubmissionConfigurationStatus,
	TemplateType,
} from "../../../common/enum";
import {
	Button,
	CircularProgress,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	Typography,
} from "../../../common/MaterialComponent";
import {
	GetMessageContentLength,
	ValidateMessagePreviewDetail,
} from "../../../hooks/useBatchMessagePreviewDetailConfiguration";
import {
	IsMessageSelected,
	ValidateMessageScheduleRecord,
} from "../../../hooks/useBatchMessageScheduleConfiguration";
import React, { useEffect, useMemo, useState } from "react";
import {
	SaveBatchMessageDraft,
	SaveBatchMessageScheduleDetail,
	SchedulePreviewBatchMessage,
} from "../../../api/batchNotificationController";

import { BatchMessageDetailPreview } from "../BatchMessageDetailPreview/BatchMessageDetailPreview";
import BatchMessagePreviewModel from "../../../models/BatchMessage/BatchMessagePreviewModel";
import BatchMessageSenderDetail from "../../../models/BatchMessage/BatchMessageSenderDetailModel";
import { ButtonPrimary } from "../../../common/sharedStyle";
import { ConfirmationDialog } from "../../Confirmation/ConfirmationDialog";
import { ErrorPage } from "../../ErrorPage/ErrorPage";
import { FORBIDDEN_RESPONSE_STATUS } from "../../../common/constants";
import { GetBatchMessagingDetail } from "../../../api/batchNotificationController";
import { GroupConfiguration } from "../GroupConfiguration/GroupConfiguration";
import MessageLanguageConfiguration from "../MessageLanguageConfiguration/MessageLanguageConfiguration";
import { MessageScheduleConfiguration } from "../MessageScheduleConfiguration/MessageScheduleConfiguration";
import { MessageSenderConfiguration } from "../MessageSenderConfiguration/MessageSenderConfiguration";
import MessageTypeSelection from "../MessageTypeSelection/MessageTypeSelection";
import { SaveGroupDetail } from "../../../api/batchNotificationController";
import { SaveMessageDetail } from "../../../api/batchNotificationController";
import { SaveMessageSenderDetail } from "../../../api/batchNotificationController";
import Shared from "../../../common/shared";
import styles from "./BatchNotificationWizard.style";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import { useHistory } from "react-router-dom";

export interface BatchNotificationWizardProp {
	rowKey: string;
	partitionKey: string;
	backToTemplateList: () => void;
}

export const BatchNotificationWizard = (props: BatchNotificationWizardProp) => {
	const classes = styles();
	const [activeStep, setActiveStep] = useState(0);
	const [highlightRequiredDetail, setHighlightRequiredDetail] = useState(false);
	const [hasStepConfigChanged, setHasStepConfigChanged] = useState(false); // variable to check if step1 config has changed
	const [hasStep2ConfigChanged, setHasStep2ConfigChanged] = useState(false); // variable to check if step2 config has changed
	const [hasStep3ConfigChanged, setHasStep3ConfigChanged] = useState(false); // variable to check if step3 config has changed
	const [hasStep4ConfigChanged, setHasStep4ConfigChanged] = useState(false); // variable to check if step4 config has changed
	const [hasStep5ConfigChanged, setHasStep5ConfigChanged] = useState(false); // variable to check if step5 config has changed
	const [batchNotificationDetail, setBatchNotificationDetail] = useState(
		new BatchMessageDetailModel()
	);
	const [isLoading, setLoading] = React.useState(false);
	const [approveTemplateLoading, setApproveTemplateLoading] = React.useState(
		false
	);
	const [rejectTemplateLoading, setRejectTemplateLoading] = React.useState(
		false
	);
	const [isMounted, setMounted] = React.useState(false);
	const [isNextButtonClick, setIsNextButtonClick] = React.useState(false); // required to open expansion panel when entered detail is invalid
	const [error, setError] = React.useState<string>("");
	const [saveAsDraftLoading, setSaveAsDraftLoading] = React.useState(false);
	const strings = useGetComponentStringsContext().BatchNotificationWizard;
	const [
		openConfirmationPopup,
		setOpenConfirmationPopup,
	] = React.useState<boolean>(false);
	const [disableActionButton, setDisableActionButton] = React.useState(false);
	const steps = [
		strings.step1Text,
		strings.step2Text,
		strings.step3Text,
		strings.step4Text,
		strings.step5Text,
		strings.step6Text,
	];
	// separate variable to manage batch message preview detail as preview detail is not stored in BatchMessageDetailModel model
	const [batchMessagePreviewDetail, setBatchMessagePreviewDetail] = useState(
		new BatchMessagePreviewModel()
	);
	const history = useHistory();

	const fullManual = process.env.REACT_APP_FULL_MANUAL_URL;

	function getStepContent(step: number) {
		switch (step) {
			case 0:
				return (
					<MessageTypeSelection
						batchNotification={batchNotificationDetail}
						onBatchNotificationChange={setBatchNotificationDetail}
						setHasStepConfigChanged={setHasStepConfigChanged}
						highlightRequiredDetail={highlightRequiredDetail}
						isReadOnly={isReadOnly()}
					/>
				);
			case 1:
				return (
					activeStep === 1 && (
						<MessageLanguageConfiguration
							batchNotification={batchNotificationDetail}
							onBatchNotificationChange={setBatchNotificationDetail}
							setHasStep2ConfigChanged={setHasStep2ConfigChanged}
							highlightRequiredDetail={highlightRequiredDetail}
							isReadOnly={isReadOnly()}
						/>
					)
				);
			case 2:
				return (
					activeStep === 2 && (
						<GroupConfiguration
							batchNotificationDetail={batchNotificationDetail}
							isReadOnly={isReadOnly()}
							setHasStep3ConfigChanged={setHasStep3ConfigChanged}
							highlightRequiredDetail={highlightRequiredDetail}
							onGroupDetailChanged={setBatchNotificationDetail}
						/>
					)
				);
			case 3:
				return (
					activeStep === 3 && (
						<MessageScheduleConfiguration
							batchNotification={batchNotificationDetail}
							onBatchNotificationChange={setBatchNotificationDetail}
							setHasStep4ConfigChanged={setHasStep4ConfigChanged}
							highlightRequiredDetail={highlightRequiredDetail}
							isNextButtonClick={isNextButtonClick}
							isReadOnly={isReadOnly()}
							resetNextButtonClick={(clicked) => setIsNextButtonClick(clicked)}
						/>
					)
				);
			case 4:
				return (
					activeStep === 4 && (
						<MessageSenderConfiguration
							batchNotificationDetail={batchNotificationDetail}
							isReadOnly={isReadOnly()}
							onBatchNotificationChange={setBatchNotificationDetail}
							setHasStep5ConfigChanged={setHasStep5ConfigChanged}
							highlightRequiredDetail={highlightRequiredDetail}
						/>
					)
				);
			case 5:
				return (
					activeStep === 5 && (
						<BatchMessageDetailPreview
							batchNotification={batchNotificationDetail}
							isReadOnly={isReadOnly()}
							highlightRequiredDetail={highlightRequiredDetail}
							onBatchMessagePreviewDetailChange={setBatchMessagePreviewDetail}
						></BatchMessageDetailPreview>
					)
				);
			default:
				return "Unknown step";
		}
	}

	const handleNext = async () => {
		switch (activeStep) {
			case 0:
				await saveBasicPreSubmissionDetail();
				break;
			case 1:
				await saveBatchMessageLanguageDetail();
				break;
			case 2:
				await saveBatchGroupDetail();
				break;
			case 3:
				await saveBatchMessageScheduleDetail();
				break;
			case 4:
				await saveMessageSenderDetail();
				break;
			case 5:
				schedulePreviewBatchMessage();
				break;
			default:
				break;
		}
	};

	const resetEditStepConfig = () => {
		//clears any old state in prep for new step or if it goes back
		setHighlightRequiredDetail(false);
		setHasStepConfigChanged(false);
	};

	const moveToNextStep = () => {
		resetEditStepConfig();
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	//saves config for step 1 or doesnt and then runs moveToNextStep
	const saveBasicPreSubmissionDetail = async () => {
		//if read only move to next step with no save
		if (isReadOnly()) {
			moveToNextStep();
			return;
		}
		//otherwise save current pre config if it has been edited and is valid
		if (
			batchNotificationDetail &&
			(!batchNotificationDetail.batchMessageType ||
				!batchNotificationDetail.title ||
				!batchNotificationDetail.country)
		) {
			//missing a field so highlight required field in child
			setHighlightRequiredDetail(true);
		} else if (hasStepConfigChanged) {
			//batchmessage has been changed so save new config
			setLoading(true);
			// API call to save Pre Configuration Detail
			const notificationDetail = await savePreConfigurationDetail();
			if (notificationDetail) {
				setBatchNotificationDetail(notificationDetail);
				moveToNextStep();
			}

			setLoading(false);
		} else {
			moveToNextStep();
		}
	};

	const checke164regex = (number: string) => {
		const e164Regex = /^\+?[1-9]\d{1,14}$/;
		return e164Regex.test(number);
	};

	const saveMessageSenderDetail = async () => {
		if (isReadOnly()) {
			moveToNextStep();
			return;
		}
		if (!batchNotificationDetail.fromNumber) {
			if (batchNotificationDetail.vodafoneEnabled) {
				const isValidNumber = checke164regex(
					batchNotificationDetail.fromNumber
				);
				setHighlightRequiredDetail(!isValidNumber);
			} else {
				setHighlightRequiredDetail(true);
			}
		} else if (hasStep5ConfigChanged) {
			// API call to save Message Sender Detail
			try {
				setLoading(true);
				const batchMessageSenderDetail: BatchMessageSenderDetail = new BatchMessageSenderDetail();
				batchMessageSenderDetail.preSubmissionConfigurationId = batchNotificationDetail.id
					? batchNotificationDetail.id
					: "";
				batchMessageSenderDetail.partitionKey =
					batchNotificationDetail.partitionKey;
				batchMessageSenderDetail.fromNumber =
					batchNotificationDetail.fromNumber;
				batchMessageSenderDetail.isAlphaNumericSenderId =
					batchNotificationDetail.isAlphaNumericSenderId;
				const res = await SaveMessageSenderDetail(batchMessageSenderDetail);
				if (res.responseObj) {
					setHasStep5ConfigChanged(false); // reset once updated detail is saved
					moveToNextStep();
				}
				Shared.showSnackbarMessage(
					res.responseObj
						? strings.saveSenderDetailSuccessMessage
						: strings.saveSenderDetailErrorMessage
				);
				setLoading(false);
			} catch (error) {
				Shared.showSnackbarMessage(strings.saveSenderDetailErrorMessage);
				setLoading(false);
			}
		} else {
			//not changed so move to next step
			moveToNextStep();
		}
	};

	const savePreConfigurationDetail = async () => {
		try {
			const configurationModel = batchNotificationDetail as PreSubmissionConfigurationModel;
			// restrict overriding of id property at the time of updating pre submission configuration detail
			configurationModel.rowKey = batchNotificationDetail.id;
			configurationModel.type = TemplateType[TemplateType.BatchMessaging];

			//Call API Request
			const res = await SavePreSubmissionConfigurationDetail(
				configurationModel
			);
			const notificationDetail: BatchMessageDetailModel = batchNotificationDetail;
			if (res.responseObj) {
				notificationDetail.id = res.responseObj.id;
				notificationDetail.title = res.responseObj.title;
				notificationDetail.partitionKey = res.responseObj.partitionKey;
				notificationDetail.africasTalkingEnabled =
					res.responseObj.africasTalkingEnabled;
				notificationDetail.fromNumber = res.responseObj.fromNumber;
				notificationDetail.currency = res.responseObj.currency;
			}

			//display a success or fail message
			Shared.showSnackbarMessage(
				res.responseObj
					? strings.savePreConfigurationDetailSuccessMessage
					: strings.savePreConfigurationDetailErrorMessage
			);

			return res.responseObj ? notificationDetail : undefined;
		} catch (err) {
			Shared.showSnackbarMessage(
				strings.savePreConfigurationDetailErrorMessage
			);
		}
	};

	const saveBatchMessageLanguageDetail = async () => {
		//if read only move to next step with no save
		if (isReadOnly()) {
			moveToNextStep();
			return;
		}
		const messageLanguageDetail =
			batchNotificationDetail.batchMessageLanguageDetail;
		if (messageLanguageDetail) {
			// validate message detail
			if (messageLanguageDetail.languages.length === 0) {
				Shared.showSnackbarMessage(strings.noLanguageSelectedText);
				return;
			}
			let isValid: boolean = true;
			for (
				let index = 0;
				index < messageLanguageDetail.languages.length;
				index++
			) {
				const messageDetail = messageLanguageDetail.languages[index];
				if (!messageDetail.messageTitle || !messageDetail.message) {
					isValid = false;
					break;
				}
			}
			if (!isValid) {
				//missing a field so highlight required field in child
				setHighlightRequiredDetail(true);
			} else if (hasStep2ConfigChanged) {
				// save current pre config if it has been edited and is valid
				setLoading(true);
				try {
					messageLanguageDetail.preSubmissionConfigurationId = batchNotificationDetail.id
						? batchNotificationDetail.id
						: "";
					messageLanguageDetail.partitionKey =
						batchNotificationDetail.partitionKey;
					const res = await SaveMessageDetail(messageLanguageDetail);
					Shared.showSnackbarMessage(
						res.responseObj
							? strings.saveLanguageDetailSuccessMessage
							: strings.saveLanguageDetailErrorMessage
					);
					const batchMessageLanguageDetail = res.responseObj;
					if (
						batchMessageLanguageDetail &&
						batchMessageLanguageDetail.languages &&
						batchMessageLanguageDetail.languages.length > 0
					) {
						batchMessageLanguageDetail.languages = Shared.SortData(
							batchMessageLanguageDetail.languages,
							"code"
						);
						const notificationDetail: BatchMessageDetailModel = {
							...batchNotificationDetail,
						};
						notificationDetail.batchMessageLanguageDetail = batchMessageLanguageDetail;
						setBatchNotificationDetail(notificationDetail);
						moveToNextStep();
						setHasStep2ConfigChanged(false);
					}
				} catch (err) {
					Shared.showSnackbarMessage(strings.saveLanguageDetailErrorMessage);
				}
				setLoading(false);
			} else {
				moveToNextStep();
			}
		}
	};

	const schedulePreviewBatchMessage = async () => {
		if (batchMessagePreviewDetail.phoneNumbers.length <= 0) {
			Shared.showSnackbarMessage(strings.noPhoneNumberAddedText);
			return;
		}
		const selectedMessages = batchMessagePreviewDetail.messages.filter(
			(m) => m.isSelected
		);
		if (selectedMessages.length === 0) {
			Shared.showSnackbarMessage(strings.noMessageSelectedText);
			return;
		}
		const isValid = ValidateMessagePreviewDetail(batchMessagePreviewDetail);
		if (!isValid) {
			setHighlightRequiredDetail(true);
		} else {
			// get message detail tp save, set message content length, remove isSelected property
			const messagePreviewDetail: BatchMessagePreviewModel = JSON.parse(
				JSON.stringify(batchMessagePreviewDetail)
			);
			messagePreviewDetail.messages.forEach(
				(message: MessageDetail, messageIndex: number) => {
					if (message.isSelected) {
						const batchMessageDetail =
							messagePreviewDetail.messages[messageIndex];
						// get message length - No. of segments, if SMS/Duration of audio recording in seconds, if Voice
						message.length = GetMessageContentLength(
							batchNotificationDetail.batchMessageLanguageDetail?.languages!,
							batchNotificationDetail.batchMessageType ===
								BatchMessageTypeEnum.Voice
								? true
								: false,
							batchMessageDetail
						);
						message.dateTime = Shared.getFormattedDate(message.dateTime);
					}
				}
			);
			messagePreviewDetail.messages = messagePreviewDetail.messages
				.filter((m) => m.isSelected)
				.map((y) => {
					delete y.isSelected;
					return y;
				});

			// API call to save message preview detail
			setLoading(true);
			try {
				const res = await SchedulePreviewBatchMessage(messagePreviewDetail);
				Shared.showSnackbarMessage(
					res.responseObj
						? strings.messageScheduleSuccessMessage
						: strings.messageScheduleErrorMessage
				);
				if (res.responseObj) {
					setActiveStep(0);
				}
				setLoading(false);
			} catch (err) {
				Shared.showSnackbarMessage(strings.messageScheduleErrorMessage);
				setLoading(false);
			}
		}
	};

	const saveBatchGroupDetail = async () => {
		if (isReadOnly()) {
			moveToNextStep();
			return;
		}
		const messageGroupDetails = batchNotificationDetail.batchMessageGroupDetail;
		if (messageGroupDetails) {
			let isValid: boolean = true;
			for (
				let index = 0;
				index < messageGroupDetails.groupDetails.length;
				index++
			) {
				const groupDetail = messageGroupDetails.groupDetails[index];
				if (!groupDetail.name || groupDetail.phoneNumberCount === 0) {
					isValid = false;
					break;
				}
			}
			if (!isValid) {
				//missing a field so highlight required field in child
				setHighlightRequiredDetail(true);
			} else if (hasStep3ConfigChanged) {
				// save current pre config if it has been edited and is valid
				setLoading(true);
				try {
					const res = await SaveGroupDetail(messageGroupDetails);
					Shared.showSnackbarMessage(
						res.responseObj
							? strings.saveGroupDetailSuccessMessage
							: strings.saveGroupDetailErrorMessage
					);
					const batchGroupDetail = res.responseObj;
					if (batchGroupDetail) {
						const notificationDetail: BatchMessageDetailModel = {
							...batchNotificationDetail,
						};
						notificationDetail.batchMessageGroupDetail = batchGroupDetail;
						setBatchNotificationDetail(notificationDetail);
						moveToNextStep();
						setHasStep3ConfigChanged(false);
					}
				} catch (err) {
					Shared.showSnackbarMessage(strings.saveGroupDetailErrorMessage);
				}
				setLoading(false);
			} else {
				moveToNextStep();
			}
		}
	};

	const saveBatchMessageScheduleDetail = async () => {
		if (isReadOnly()) {
			moveToNextStep();
			return;
		}
		const messageScheduleDetail: BatchMessageSchedulingDetailModel | null =
			batchNotificationDetail.batchMessageSchedulingDetail;
		let isMessageSelected: boolean = false;
		if (messageScheduleDetail) {
			isMessageSelected = IsMessageSelected(messageScheduleDetail.messages);
			if (!isMessageSelected) {
				Shared.showSnackbarMessage(strings.noMessageSelectedText);
				return;
			}
			if (!ValidateMessageScheduleRecord(messageScheduleDetail.messages)) {
				//missing a field so highlight required field in child
				setHighlightRequiredDetail(true);
				// set value of isNextButtonClick variable to open expansion panel containing invalid detail as value of highlight detail variable will not be change every next button click.
				setIsNextButtonClick(true);
			} else if (hasStep4ConfigChanged) {
				try {
					setLoading(true);
					messageScheduleDetail.messages = messageScheduleDetail.messages.map(
						(messageDetail: MessageDetail) => {
							delete messageDetail.isSelected;
							return messageDetail;
						}
					);
					messageScheduleDetail.preSubmissionConfigurationId = batchNotificationDetail.id
						? batchNotificationDetail.id
						: "";
					messageScheduleDetail.partitionKey =
						batchNotificationDetail.partitionKey;
					messageScheduleDetail.messages.forEach((message) => {
						message.recurrenceDetails?.days.sort();
					});
					const res = await SaveBatchMessageScheduleDetail(
						messageScheduleDetail
					);
					Shared.showSnackbarMessage(
						res.responseObj
							? strings.saveScheduleDetailSuccessMessage
							: strings.saveScheduleDetailErrorMessage
					);
					const batchMessageScheduleDetail = res.responseObj;
					if (batchMessageScheduleDetail) {
						const notificationDetail: BatchMessageDetailModel = {
							...batchNotificationDetail,
						};
						notificationDetail.batchMessageSchedulingDetail = batchMessageScheduleDetail;
						setBatchNotificationDetail(notificationDetail);
						moveToNextStep();
						setHasStep4ConfigChanged(false);
					}
				} catch (err) {
					setLoading(false);
					Shared.showSnackbarMessage(strings.saveScheduleDetailErrorMessage);
				}
				setLoading(false);
			} else {
				moveToNextStep();
			}
		}
	};

	const saveBatchMessageDraft = async () => {
		// set data in BatchMessageDraftModel
		const batchMessageDraftModel: BatchMessageDraftModel = new BatchMessageDraftModel();
		batchMessageDraftModel.preSubmissionConfigurationId = batchNotificationDetail.id
			? batchNotificationDetail.id
			: "";
		batchMessageDraftModel.partitionKey = batchNotificationDetail.partitionKey;
		if (batchNotificationDetail.batchMessageSchedulingDetail!) {
			batchNotificationDetail.batchMessageSchedulingDetail.messages.forEach(
				(messageDetail, messageIndex) => {
					const message: Message = new Message();
					// get group phone number count
					const groupDetail = batchNotificationDetail.batchMessageGroupDetail?.groupDetails.find(
						(g) => g.id === messageDetail.groupId
					);
					message.phoneNumberCount = groupDetail
						? groupDetail.phoneNumberCount
						: 0;
					message.id = messageDetail.id; // get message id
					// get message length - No. of segments, if SMS/Duration of audio recording in seconds, if Voice
					const batchMessageDetail = batchNotificationDetail.batchMessageSchedulingDetail!
						.messages[messageIndex];
					message.length = GetMessageContentLength(
						batchNotificationDetail.batchMessageLanguageDetail?.languages!,
						batchNotificationDetail.batchMessageType ===
							BatchMessageTypeEnum.Voice
							? true
							: false,
						batchMessageDetail
					);
					batchMessageDraftModel.messages.push(message);
				}
			);
		}
		setSaveAsDraftLoading(true);
		try {
			const res = await SaveBatchMessageDraft(batchMessageDraftModel);
			Shared.showSnackbarMessage(
				res.responseObj
					? strings.saveAsDraftSuccessMessage
					: strings.saveAsDraftErrorMessage
			);

			if (!res.error) {
				const notificationDetail: BatchMessageDetailModel = {
					...batchNotificationDetail,
				};
				// set PreSubmissionConfigurationStatus to submitted - make all fields as readOnly
				notificationDetail.status! = PreSubmissionConfigurationStatus[
					PreSubmissionConfigurationStatus.Submitted
				];
				setBatchNotificationDetail(notificationDetail);
				setActiveStep(0);
			}
			setSaveAsDraftLoading(false);
		} catch (err) {
			Shared.showSnackbarMessage(strings.saveAsDraftErrorMessage);
			setSaveAsDraftLoading(false);
		}
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
		setSaveAsDraftLoading(false);
		setLoading(false);
	};

	function isReadOnly(): boolean {
		return batchNotificationDetail.status !== undefined
			? batchNotificationDetail.status.toString() ===
					PreSubmissionConfigurationStatus[
						PreSubmissionConfigurationStatus.Approved
					] ||
					batchNotificationDetail.status.toString() ===
						PreSubmissionConfigurationStatus[
							PreSubmissionConfigurationStatus.Submitted
						]
			: false;
	}

	const isStatusSubmitted = useMemo(
		() => () => {
			return batchNotificationDetail.status !== undefined
				? batchNotificationDetail.status.toString() ===
						PreSubmissionConfigurationStatus[
							PreSubmissionConfigurationStatus.Submitted
						]
				: false;
		},
		[batchNotificationDetail.status]
	);

	useEffect(() => {
		const getBatchMessagingDetail = async (
			partitionKey: string,
			rowKey: string
		) => {
			if (!partitionKey || !rowKey) {
				setMounted(true);
				return;
			}
			try {
				const res = await GetBatchMessagingDetail(partitionKey, rowKey);
				if (res.responseObj) {
					setBatchNotificationDetail(res.responseObj);
					setMounted(true);
					handleReset();
				} else {
					if (res.error?.includes(FORBIDDEN_RESPONSE_STATUS.toString())) {
						// response status is 403, redirect to access denied page
						history.push({
							pathname: "/accessDenied",
						});
					}
					setError(res.error!);
				}
			} catch (err) {
				setError(err as string);
			}
		};

		setMounted(false);
		getBatchMessagingDetail(props.partitionKey, props.rowKey);
	}, [history, props.partitionKey, props.rowKey]);

	const approveTemplateAsync = async (id: string) => {
		try {
			setApproveTemplateLoading(true);
			const res = await ApproveTemplateAsync(
				id,
				batchNotificationDetail.partitionKey
			);
			setApproveTemplateLoading(false);
			Shared.showSnackbarMessage(
				res.error ? res.error : strings.approveTemplateSuccessMessage
			);
			if (!res.error) {
				setDisableActionButton(true);
				props.backToTemplateList();
			}
		} catch (err) {
			setApproveTemplateLoading(false);
			Shared.showSnackbarMessage(strings.approveTemplateErrorMessage);
		}
	};

	const rejectTemplateAsync = () => {
		setOpenConfirmationPopup(true);
	};

	const onConfirmRejectTemplate = async (rejectTemplate: boolean = false) => {
		if (rejectTemplate) {
			try {
				setRejectTemplateLoading(true);
				const res = await RejectTemplateAsync(
					batchNotificationDetail.id,
					batchNotificationDetail.partitionKey
				);
				setRejectTemplateLoading(false);
				setOpenConfirmationPopup(false);
				Shared.showSnackbarMessage(
					res.error
						? strings.rejectTemplateErrorMessage
						: strings.rejectTemplateSuccessMessage
				);
				if (!res.error) {
					setDisableActionButton(true);
					props.backToTemplateList();
				}
			} catch (err) {
				Shared.showSnackbarMessage(strings.rejectTemplateErrorMessage);
				setOpenConfirmationPopup(false);
			}
		} else {
			setOpenConfirmationPopup(false);
		}
	};

	return (
		<div className={classes.root} data-testid="batchWizardContainer">
			{error ? (
				<ErrorPage message={strings.notificationDetailErrorMessage} />
			) : !isMounted ? (
				<div className="loader">
					<CircularProgress size={50} />
				</div>
			) : (
				<>
					{fullManual ? (
						<span className={classes.fullManualText}>
							{strings.readManualText}{" "}
							<a href={fullManual} target="_blank" rel="noopener noreferrer">
								{strings.hereText}
							</a>
						</span>
					) : null}
					<Stepper
						activeStep={activeStep}
						orientation="vertical"
						data-testid="batchStepper"
					>
						{steps.map((label, index) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
								<StepContent>
									<Typography component="div">
										{getStepContent(index)}
									</Typography>
									<div className={classes.actionsContainer}>
										<div>
											{activeStep !== 0 && (
												<Button
													data-testid="backBtn"
													disabled={
														activeStep === 0 || isLoading || saveAsDraftLoading
													}
													onClick={handleBack}
													className={classes.button}
												>
													{strings.backButton}
												</Button>
											)}
											{(
												isReadOnly() ? activeStep !== steps.length - 1 : true
											) ? (
												<>
													<span className={classes.actionBtnDiv}>
														<ButtonPrimary
															variant="contained"
															data-testid="nextBtn"
															onClick={handleNext}
															className={
																classes.actionButton +
																(isLoading ||
																saveAsDraftLoading ||
																(activeStep === steps.length - 1 &&
																	isReadOnly())
																	? " disabledBtn"
																	: "")
															}
														>
															{activeStep === steps.length - 1
																? strings.testButton
																: strings.nextButton}
														</ButtonPrimary>
														{isLoading && (
															<CircularProgress
																size={24}
																className={classes.btnLoader}
																color="primary"
															/>
														)}
													</span>
													<span className={classes.actionBtnDiv}>
														{activeStep === steps.length - 1 && (
															<>
																<ButtonPrimary
																	variant="contained"
																	onClick={saveBatchMessageDraft}
																	data-testid="saveAsDraftBtn"
																	className={
																		classes.actionButton +
																		(isLoading ||
																		saveAsDraftLoading ||
																		isReadOnly()
																			? " disabledBtn"
																			: "")
																	}
																>
																	{strings.saveAsDraftButton}
																</ButtonPrimary>
																{saveAsDraftLoading && (
																	<CircularProgress
																		size={24}
																		className={classes.draftBtnLoader}
																		color="primary"
																	/>
																)}
															</>
														)}
													</span>
												</>
											) : (
												""
											)}
											{isStatusSubmitted() &&
											Shared.canApproveOrRejectTemplate() ? (
												<>
													<span className={classes.actionBtnDiv}>
														{activeStep === steps.length - 1 && (
															<>
																<Button
																	variant="contained"
																	data-testid="approveBtn"
																	onClick={() => {
																		approveTemplateAsync(props.rowKey);
																	}}
																	disabled={
																		approveTemplateLoading ||
																		rejectTemplateLoading ||
																		disableActionButton
																	}
																	className={
																		classes.approveBtn +
																		(isLoading ? " disabledBtn" : "")
																	}
																>
																	{strings.approveButton}
																</Button>
																{approveTemplateLoading && (
																	<CircularProgress
																		size={24}
																		className={classes.approveBtnLoader}
																		color="primary"
																	/>
																)}
															</>
														)}
													</span>
													<span className={classes.actionBtnDiv}>
														{activeStep === steps.length - 1 && (
															<>
																<Button
																	variant="contained"
																	data-testid="rejectBtn"
																	onClick={() => {
																		rejectTemplateAsync();
																	}}
																	disabled={
																		approveTemplateLoading ||
																		rejectTemplateLoading ||
																		disableActionButton
																	}
																	className={
																		classes.rejectButton +
																		(isLoading ? " disabledBtn" : "")
																	}
																>
																	{strings.rejectButton}
																</Button>
																{rejectTemplateLoading && (
																	<CircularProgress
																		size={24}
																		className={classes.approveBtnLoader}
																		color="primary"
																	/>
																)}
															</>
														)}
													</span>
												</>
											) : (
												""
											)}
										</div>
									</div>
								</StepContent>
							</Step>
						))}
					</Stepper>
					<ConfirmationDialog
						open={openConfirmationPopup}
						message={strings.rejectTemplateConfirmationMessage}
						closeDialog={(isDeleteBtnPressed?: boolean) =>
							onConfirmRejectTemplate(isDeleteBtnPressed)
						}
						positiveActionText={strings.deleteButtonText}
						negativeActionText={strings.cancelButtonText}
					></ConfirmationDialog>
				</>
			)}
		</div>
	);
};
